import axios from "axios";
import api from "./api";
import {
  ensureTaskerAuth,
  taskerCfg,
  type TaskerAuthMode,
} from "./taskerConfig";

export type AttachmentAuthMode = TaskerAuthMode;

export type UploadUrlSlot = {
  url: string;
  attachment_id: string;
  file_name: string;
};

export type ConfirmedAttachment = {
  id: string;
  attachment_id: string;
  name: string;
  gcs_object_path: string;
  owner_reference?: string;
  owner_type?: string;
  created_at?: string;
  updated_at?: string;
};

export type ConfirmAttachmentInput = {
  attachment_id: string;
  file_name: string;
  req_id?: string;
  type?: string;
};

export async function generateUploadUrl(
  fileName: string,
  authMode: AttachmentAuthMode = "vendor",
): Promise<UploadUrlSlot> {
  await ensureTaskerAuth(authMode);
  const response = (await api.get(
    "/attachment/uploads/generate-url",
    { ...taskerCfg(authMode), params: { fileName } },
  )) as { data?: UploadUrlSlot[] } | null;
  const slot = response?.data?.[0];
  if (!slot?.url || !slot?.attachment_id) {
    throw new Error("generateUploadUrl: malformed response");
  }
  return slot;
}

export async function putToGcs(
  url: string,
  file: File,
  signal?: AbortSignal,
): Promise<void> {
  await axios.put(url, file, {
    headers: { "Content-Type": file.type || "application/octet-stream" },
    signal,
  });
}

export async function confirmAttachment(
  input: ConfirmAttachmentInput,
  authMode: AttachmentAuthMode = "vendor",
): Promise<ConfirmedAttachment> {
  await ensureTaskerAuth(authMode);
  const body: Record<string, unknown> = {
    attachment_id: input.attachment_id,
    file_name: input.file_name,
  };
  if (input.req_id) body.req_id = input.req_id;
  if (input.type) body.type = input.type;

  const response = (await api.post(
    "/attachment/uploads/confirm",
    body,
    taskerCfg(authMode),
  )) as { data?: ConfirmedAttachment[] } | null;

  const confirmed = response?.data?.[0];
  if (!confirmed?.attachment_id) {
    throw new Error("confirmAttachment: malformed response");
  }
  return confirmed;
}

export async function generateDownloadUrl(
  attachmentId: string,
  authMode: AttachmentAuthMode = "vendor",
): Promise<string> {
  await ensureTaskerAuth(authMode);
  const response = (await api.get(
    `/attachment/downloads/${encodeURIComponent(attachmentId)}/download-url`,
    taskerCfg(authMode),
  )) as { data?: string[] } | null;
  const url = response?.data?.[0];
  if (!url) throw new Error("generateDownloadUrl: malformed response");
  return url;
}

const taskerAttachments = {
  generateUploadUrl,
  putToGcs,
  confirmAttachment,
  generateDownloadUrl,
};

export default taskerAttachments;
